import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
} from "@angular/core";
import Chart from "chart.js/auto";
import { ChartConfiguration, ChartDataset } from "chart.js";

import { Widget } from "../../../dashboard/models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";

// ⬇️ imports pour le bouton filtre dans le header
import { SharedModule } from "../../../../shared/shared.module";
import { PopoverModule } from "../../../../shared/modules/popover/popover.module";
import { TiersFacadeService } from "../../services/tiers/tiers-facade.service";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import {
  CaCumuleDialogComponent,
  CaCumuleDialogData,
  CaCumulePeriodRow,
} from "./ca-cumule-dialog.component";

type TimeScale = "DAY" | "WEEK" | "MONTH";

@Component({
  selector: "vl-ca-cumule-tiers",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    PopoverModule,
    WidgetTitleComponent,
    ChipsComponent,
    ChipsToggleComponent,
  ],
  styleUrls: ["./ca-cumule-tiers.component.scss"],
  templateUrl: "./ca-cumule-tiers.component.html",
})
export class WidgetCaCumuleTiersComponent implements OnDestroy {
  // ==== Inputs ====
  private readonly tiersFacade = inject(TiersFacadeService);
  private readonly dialogsService = inject(DialogsService);
  stats = this.tiersFacade.getState().stats;
  widget = input<Widget>();
  metadata = input<EntityMetadata>();

  // ==== UI / Chart ====
  private readonly elRef = inject(ElementRef<HTMLElement>);
  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("c");
  chart?: Chart<"line", number[], string>;

  // ==== Filtres ====
  timeScale = signal<TimeScale>("MONTH");
  timeScaleChoices = [
    { name: "Jour", value: "DAY" },
    { name: "Semaine", value: "WEEK" },
    { name: "Mois", value: "MONTH" },
  ];

  // ==== Formatters ====
  private fmtEUR = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });

  // ==== Données ====
  /** Série cumulée + zero-fill entre min/max (DAY/WEEK(ISO)/MONTH) */
  points = computed(() => {
    const lignes = (this.stats()?.facturesLignes ?? []) as any[];
    const scale = this.timeScale();

    const byKey = new Map<string, { value: number; d: Date }>();

    for (const fl of lignes) {
      const f = fl?.facture;
      const dRaw = parseDate(f?.date ?? f?.dateString ?? fl?.createdAt);
      if (!dRaw) continue;

      const d = startOf(dRaw, scale);
      const key = makeKey(d, scale);

      const q = parseNumber(fl?.quantite ?? 0);
      const pu = parseNumber(fl?.prix ?? 0);
      const base = q * pu;

      const amount = base;
      if (!amount) continue;

      const prev = byKey.get(key);
      if (prev) prev.value += amount;
      else byKey.set(key, { value: amount, d });
    }

    if (!byKey.size) return [] as { label: string; cum: number }[];

    // Min/max range
    const vals = [...byKey.values()].sort(
      (a, b) => a.d.getTime() - b.d.getTime(),
    );
    const minD = vals[0].d;
    const maxD = vals.at(-1)!.d;

    // Timeline (zero-fill)
    const timeline: Date[] = [];
    for (let d = new Date(minD); d <= maxD; d = addOne(d, scale)) {
      timeline.push(new Date(d));
    }

    let acc = 0;
    return timeline.map((d) => {
      const key = makeKey(d, scale);
      const v = byKey.get(key)?.value ?? 0;
      acc += v;
      return { label: formatKey(d, scale), cum: Math.round(acc * 100) / 100 };
    });
  });

  labels = computed(() => this.points().map((p) => p.label));
  private series = computed(() => this.points().map((p) => p.cum));
  totalDisplay = computed(() => this.fmtEUR.format(this.series().at(-1) ?? 0));

  constructor() {
    // (Re)build chart quand données ET canvas existent (pattern composant “qui marche”)
    effect(
      () => {
        if (this.canvasRef()) this.buildChart();
      },
      { allowSignalWrites: true },
    );

    // MAJ chart (données + styles) réactive
    effect(
      () => {
        if (!this.chart || !this.canvasRef()) return;

        // Labels + data
        this.chart.data.labels = this.labels();
        const ds = this.chart.data.datasets[0] as ChartDataset<
          "line",
          number[]
        >;
        ds.data = this.series();

        // Couleurs (fallback si variables CSS absentes)
        const styles = getComputedStyle(this.elRef.nativeElement);
        const bgAlt =
          styles.getPropertyValue("--color-on-background-alt").trim() || "#666";
        const accent =
          styles.getPropertyValue("--color-accent").trim() || "#8a2be2";
        const onAccent =
          styles.getPropertyValue("--color-on-accent").trim() || "#fff";

        (ds as any).borderColor = accent;
        (ds as any).pointBackgroundColor = accent;
        (ds as any).pointBorderColor = onAccent;
        (this.chart.options.scales!.x as any).ticks.color = bgAlt;
        (this.chart.options.scales!.y as any).ticks.color = bgAlt;

        this.chart.update("none");
      },
      { allowSignalWrites: true },
    );
  }

  private buildChart() {
    const canvas = this.canvasRef();
    if (!canvas) return;
    const ctx = canvas.nativeElement.getContext("2d");
    if (!ctx) return;

    const styles = getComputedStyle(this.elRef.nativeElement);
    const bgAlt =
      styles.getPropertyValue("--color-on-background-alt").trim() || "#666";
    const accent =
      styles.getPropertyValue("--color-accent").trim() || "#8a2be2";
    const onAccent =
      styles.getPropertyValue("--color-on-accent").trim() || "#fff";

    const config: ChartConfiguration<"line", number[], string> = {
      type: "line",
      data: {
        labels: this.labels(),
        datasets: [
          {
            label: "CA cumulé",
            data: this.series(),
            borderWidth: 3,
            borderColor: accent,
            backgroundColor: `${accent}20`,
            pointBackgroundColor: accent,
            pointBorderColor: onAccent,
            pointBorderWidth: 2,
            pointRadius: 2,
            fill: true,
            tension: 0.2,
            spanGaps: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: bgAlt, maxRotation: 0, autoSkip: true },
          },
          y: {
            beginAtZero: true,
            grace: "5%",
            grid: { color: "#e0e0e0" },
            ticks: {
              color: bgAlt,
              callback: (v: any) =>
                parseNumber(v).toLocaleString("fr-FR", {
                  maximumFractionDigits: 0,
                }) + " €",
            },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) =>
                " " + this.fmtEUR.format(parseNumber(ctx.parsed.y) || 0),
            },
          },
        },
      },
    };

    this.chart?.destroy();
    this.chart = new Chart<"line", number[], string>(ctx, config);
  }

  openFullList() {
    const pts = this.points();
    if (!pts.length) return;
    // Reconstitue les rows avec dateKey (à partir du label on n'a pas la clé propre,
    // mais pour l'affichage antechronologique dans le dialog on se base sur le label,
    // qui est déjà unique par période — le dialog recalcule de toutes manières quand
    // l'échelle change via `facturesLignes` brutes).
    const rows: CaCumulePeriodRow[] = pts.map((p) => {
      // Reconstituer ca de la période : p.cum est cumul, il faut le différence précédente
      return { label: p.label, dateKey: p.label, ca: 0, cumul: p.cum };
    });
    // Recalcule le CA période (delta) à partir du cumul
    for (let i = 0; i < rows.length; i++) {
      rows[i].ca = Math.round(((rows[i].cumul - (i > 0 ? rows[i - 1].cumul : 0)) * 100)) / 100;
    }

    const data: CaCumuleDialogData = {
      tiersDenomination: this.stats()?.tiers?.denomination ?? "",
      rows,
      scale: this.timeScale(),
      facturesLignes: this.stats()?.facturesLignes ?? [],
    };

    this.dialogsService.open(
      CaCumuleDialogComponent,
      "min(95vw, 820px)",
      "fit-content",
      data,
    );
  }

  ngOnDestroy() {
    this.chart?.destroy();
  }
}

/* ===== Helpers temps ===== */

function parseDate(v: any): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function startOf(d: Date, scale: TimeScale): Date {
  const dd = new Date(d);
  if (scale === "DAY") {
    dd.setHours(0, 0, 0, 0);
    return dd;
  }
  if (scale === "MONTH") {
    return new Date(dd.getFullYear(), dd.getMonth(), 1, 0, 0, 0, 0);
  }
  // WEEK ISO -> Lundi
  return startOfISOWeek(dd);
}

function addOne(d: Date, scale: TimeScale): Date {
  const dd = new Date(d);
  if (scale === "DAY") {
    dd.setDate(dd.getDate() + 1);
    return startOf(dd, "DAY");
  }
  if (scale === "MONTH") {
    return new Date(dd.getFullYear(), dd.getMonth() + 1, 1, 0, 0, 0, 0);
  }
  // WEEK ISO +7 jours, aligné lundi
  dd.setDate(dd.getDate() + 7);
  return startOfISOWeek(dd);
}

function startOfISOWeek(date: Date): Date {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // 0 = lundi
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - day);
  return d;
}

function isoWeek(date: Date): { year: number; week: number } {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const dayNum = (d.getUTCDay() + 6) % 7; // 0=lundi
  d.setUTCDate(d.getUTCDate() - dayNum + 3); // jeudi
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week =
    1 +
    Math.round(((d.getTime() - firstThursday.getTime()) / 86400000 - 3) / 7);
  return { year: d.getUTCFullYear(), week };
}

function makeKey(d: Date, scale: TimeScale): string {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  if (scale === "DAY") return `${y}-${m}-${day}`;
  if (scale === "MONTH") return `${y}-${m}`;
  const { year, week } = isoWeek(d);
  return `${year}-W${week.toString().padStart(2, "0")}`;
}

function formatKey(d: Date, scale: TimeScale): string {
  if (scale === "DAY") {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
    }).format(d);
  }
  if (scale === "MONTH") {
    return new Intl.DateTimeFormat("fr-FR", {
      month: "2-digit",
      year: "numeric",
    }).format(d);
  }
  const { year, week } = isoWeek(d);
  return `S${week.toString().padStart(2, "0")} ${year}`;
}
